import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Space,
  Modal,
  Table,
  Tag,
  message,
  Spin,
  Descriptions,
  Empty,
  Divider,
  Collapse,
  Tabs,
} from "antd";
import {
  SearchOutlined,
  LinkOutlined,
  CheckOutlined,
  CloseOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { Company } from "../types/company";
import {
  LeanCustomsService,
  LeanCustomsOrganization,
  LeanCustomsContract,
  LeanCustomsContractSigner,
  LeanCustomsContractSupportDocument,
  LeanCustomsDeclaration,
  LeanCustomsInvoice,
} from "../services/leancustomsService";
import {
  DECLARATION_STATUS_ORDER,
  DECLARATION_STATUS_COLORS,
  DECLARATION_STATUS_LABELS,
} from "../types/leancustoms";
import { supabase } from "../lib/supabase";
import { BookingReferenceText } from "./BookingReferenceLink";
import { leanCustomsDeclarationMatchesBooking } from "../utils/bookingUtils";
import { LEANCUSTOMS_ORGANIZATION_BASE, CUSTOMS_PAGE_ENABLED } from "../constants/leancustoms";
import { filterDeclarationsForCompanyOrg } from "../utils/leanCustomsDeclarationAccess";

interface AdminLeanCustomsIntegrationSectionProps {
  company: Company;
  onRefresh?: () => void;
  /** When true, hides Link/Unlink/Change actions (e.g. when embedded in booking details). */
  readOnly?: boolean;
  /** When set, only show declarations whose reference/booking_number matches this booking (EFF pattern). */
  bookingId?: number;
  /** Increment to force reload of declarations (e.g. after creating one). */
  refreshTrigger?: number;
}

const AdminLeanCustomsIntegrationSection: React.FC<
  AdminLeanCustomsIntegrationSectionProps
> = ({ company, onRefresh, readOnly = false, bookingId, refreshTrigger }) => {
  const [searchModalVisible, setSearchModalVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [organizations, setOrganizations] = useState<LeanCustomsOrganization[]>(
    []
  );
  const [suggestedOrganizations, setSuggestedOrganizations] = useState<
    LeanCustomsOrganization[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [selectedOrg, setSelectedOrg] =
    useState<LeanCustomsOrganization | null>(null);
  const [viewOrgModalVisible, setViewOrgModalVisible] = useState(false);
  const [linking, setLinking] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const [linkedOrg, setLinkedOrg] = useState<LeanCustomsOrganization | null>(
    null
  );
  const [loadingLinkedOrg, setLoadingLinkedOrg] = useState(false);
  const [contracts, setContracts] = useState<LeanCustomsContract[]>([]);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [contractSigners, setContractSigners] = useState<
    Record<string, LeanCustomsContractSigner[]>
  >({});
  const [loadingSigners, setLoadingSigners] = useState<Record<string, boolean>>(
    {}
  );
  const [contractSupportDocuments, setContractSupportDocuments] = useState<
    Record<string, LeanCustomsContractSupportDocument[]>
  >({});
  const [loadingSupportDocuments, setLoadingSupportDocuments] = useState<
    Record<string, boolean>
  >({});
  const [selectedSigner, setSelectedSigner] =
    useState<LeanCustomsContractSigner | null>(null);
  const [signerModalVisible, setSignerModalVisible] = useState(false);
  const [loadingSignerDetails, setLoadingSignerDetails] = useState(false);
  const [declarations, setDeclarations] = useState<LeanCustomsDeclaration[]>(
    []
  );
  const [loadingDeclarations, setLoadingDeclarations] = useState(false);
  const [selectedDeclaration, setSelectedDeclaration] =
    useState<LeanCustomsDeclaration | null>(null);
  const [declarationModalVisible, setDeclarationModalVisible] = useState(false);
  const [declarationInvoices, setDeclarationInvoices] = useState<
    LeanCustomsInvoice[]
  >([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  // Load linked organization details when component mounts or company changes
  useEffect(() => {
    if (company.leancustoms_organization_id) {
      loadLinkedOrganization();
      loadContracts();
      loadDeclarations();
    } else {
      setLinkedOrg(null);
      setContracts([]);
      setContractSigners({});
      setContractSupportDocuments({});
      setDeclarations([]);
    }
  }, [company.leancustoms_organization_id, bookingId, refreshTrigger]);

  const loadLinkedOrganization = async () => {
    if (!company.leancustoms_organization_id) return;

    setLoadingLinkedOrg(true);
    try {
      // Pass company_id for access control
      const result = await LeanCustomsService.getOrganization(
        company.leancustoms_organization_id,
        company.id // Include company_id for access control
      );
      if (result.success && result.data) {
        setLinkedOrg(result.data);
      }
    } catch (error) {
      console.error("Error loading linked organization:", error);
    } finally {
      setLoadingLinkedOrg(false);
    }
  };

  const loadContracts = async () => {
    if (!company.leancustoms_organization_id) return;

    setLoadingContracts(true);
    try {
      const result = await LeanCustomsService.listOrganizationContracts(
        company.leancustoms_organization_id,
        company.id,
        { page: 1, page_size: 100 }
      );
      if (result.success && result.data) {
        setContracts(result.data.results);
      }
    } catch (error) {
      console.error("Error loading contracts:", error);
    } finally {
      setLoadingContracts(false);
    }
  };

  const loadDeclarations = async () => {
    if (!company.leancustoms_organization_id) return;

    setLoadingDeclarations(true);
    try {
      const result = await LeanCustomsService.listDeclarations(
        company.leancustoms_organization_id,
        company.id,
        { page: 1, page_size: 100 }
      );
      if (result.success && result.data) {
        const list = filterDeclarationsForCompanyOrg(
          result.data.results,
          company.leancustoms_organization_id
        );
        const filtered =
          bookingId != null
            ? list.filter((d) => leanCustomsDeclarationMatchesBooking(d, bookingId))
            : list;
        setDeclarations(filtered);
      }
    } catch (error) {
      console.error("Error loading declarations:", error);
    } finally {
      setLoadingDeclarations(false);
    }
  };

  const loadDeclarationInvoices = async (declarationId: string) => {
    if (!company.id) return;
    setLoadingInvoices(true);
    try {
      const result = await LeanCustomsService.listDeclarationInvoices(
        declarationId,
        company.id
      );
      if (result.success && result.data) {
        setDeclarationInvoices(result.data.results);
      } else {
        setDeclarationInvoices([]);
        if (result.error) {
          message.warning(result.error);
        }
      }
    } catch (error) {
      console.error("Error loading declaration invoices:", error);
      setDeclarationInvoices([]);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const loadContractSigners = async (contractId: string) => {
    if (contractSigners[contractId]) {
      // Already loaded
      return;
    }

    setLoadingSigners({ ...loadingSigners, [contractId]: true });
    try {
      const result = await LeanCustomsService.listContractSigners(
        contractId,
        company.id,
        { page: 1, page_size: 100 }
      );
      if (result.success && result.data) {
        setContractSigners({
          ...contractSigners,
          [contractId]: result.data.results,
        });
      }
    } catch (error) {
      console.error("Error loading contract signers:", error);
    } finally {
      setLoadingSigners({ ...loadingSigners, [contractId]: false });
    }
  };

  const loadContractSupportDocuments = async (contractId: string) => {
    if (contractSupportDocuments[contractId]) {
      // Already loaded
      return;
    }

    setLoadingSupportDocuments({
      ...loadingSupportDocuments,
      [contractId]: true,
    });
    try {
      const result = await LeanCustomsService.listContractSupportDocuments(
        contractId,
        company.id
      );
      if (result.success && result.data) {
        setContractSupportDocuments({
          ...contractSupportDocuments,
          [contractId]: result.data.results,
        });
      }
    } catch (error) {
      console.error("Error loading contract support documents:", error);
    } finally {
      setLoadingSupportDocuments({
        ...loadingSupportDocuments,
        [contractId]: false,
      });
    }
  };

  const handleDownloadDocument = async (
    contractId: string,
    supportDocument: LeanCustomsContractSupportDocument
  ) => {
    try {
      const blob = await LeanCustomsService.downloadContractSupportDocument(
        contractId,
        supportDocument.id,
        company.id
      );

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement("a");
      a.href = url;
      a.download = supportDocument.filename;
      window.document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      window.document.body.removeChild(a);

      message.success("Document downloaded successfully");
    } catch (error) {
      console.error("Error downloading document:", error);
      message.error("Failed to download document");
    }
  };

  const handleViewSignerDetails = async (signer: LeanCustomsContractSigner) => {
    if (!signer.id) {
      message.warning("Signer ID not available");
      return;
    }

    setSignerModalVisible(true);
    setLoadingSignerDetails(true);
    try {
      const result = await LeanCustomsService.getContractSigner(
        signer.id,
        company.id
      );
      if (result.success && result.data) {
        setSelectedSigner(result.data);
      } else {
        message.error(result.error || "Failed to load signer details");
        setSignerModalVisible(false);
      }
    } catch (error) {
      console.error("Error loading signer details:", error);
      message.error("Failed to load signer details");
      setSignerModalVisible(false);
    } finally {
      setLoadingSignerDetails(false);
    }
  };

  const searchOrganizations = async (page: number = 1) => {
    setLoading(true);
    try {
      // Pass company_id for access control (admin can search all, customers only their company)
      const result = await LeanCustomsService.listOrganizations({
        page,
        page_size: pagination.pageSize,
        q: searchQuery || undefined,
        company_id: company.id, // Include company_id for access control
      });

      if (result.success && result.data) {
        setOrganizations(result.data.results);
        setPagination({
          ...pagination,
          current: page,
          total: result.data.pagination.total,
        });
      } else {
        message.error(result.error || "Failed to search organizations");
        setOrganizations([]);
      }
    } catch (error) {
      console.error("Error searching organizations:", error);
      message.error("Failed to search organizations");
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  };

  const presearchOrganizations = async () => {
    setLoadingSuggestions(true);
    try {
      // Search for organizations matching company's COC and EORI numbers
      const searches: Promise<any>[] = [];

      if (company.coc_number) {
        searches.push(
          LeanCustomsService.listOrganizations({
            page: 1,
            page_size: 10,
            coc_number: String(company.coc_number),
            company_id: company.id,
          })
        );
      }

      if (company.eori_number) {
        searches.push(
          LeanCustomsService.listOrganizations({
            page: 1,
            page_size: 10,
            eori_number: company.eori_number,
            company_id: company.id,
          })
        );
      }

      if (searches.length === 0) {
        message.info("No COC or EORI number available for matching");
        setLoadingSuggestions(false);
        return;
      }

      const results = await Promise.all(searches);
      const allSuggestions: LeanCustomsOrganization[] = [];

      results.forEach((result) => {
        if (result.success && result.data) {
          allSuggestions.push(...result.data.results);
        }
      });

      // Remove duplicates and prioritize exact matches
      const uniqueSuggestions = Array.from(
        new Map(allSuggestions.map((org) => [org.id, org])).values()
      );

      // Sort by match quality (exact COC + EORI match first, then COC, then EORI)
      uniqueSuggestions.sort((a, b) => {
        const aCocMatch =
          company.coc_number && a.coc_number === String(company.coc_number);
        const aEoriMatch =
          company.eori_number && a.eori_number === company.eori_number;
        const bCocMatch =
          company.coc_number && b.coc_number === String(company.coc_number);
        const bEoriMatch =
          company.eori_number && b.eori_number === company.eori_number;

        const aScore = (aCocMatch ? 2 : 0) + (aEoriMatch ? 2 : 0);
        const bScore = (bCocMatch ? 2 : 0) + (bEoriMatch ? 2 : 0);

        return bScore - aScore;
      });

      setSuggestedOrganizations(uniqueSuggestions);

      if (uniqueSuggestions.length > 0) {
        message.success(
          `Found ${uniqueSuggestions.length} matching organization(s)`
        );
      } else {
        message.info("No matching organizations found");
      }
    } catch (error) {
      console.error("Error in presearch:", error);
      message.error("Failed to find matching organizations");
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleSearch = () => {
    searchOrganizations(1);
  };

  const handleLinkOrganization = async (
    organization: LeanCustomsOrganization
  ) => {
    setLinking(true);
    try {
      const result = await LeanCustomsService.linkOrganization(
        organization.id,
        company.id
      );

      if (!result.success) {
        throw new Error(result.error || "Failed to link organization");
      }

      message.success("Organization linked successfully");
      setSearchModalVisible(false);
      setSelectedOrg(null);
      setLinkedOrg(organization);

      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error("Error linking organization:", error);
      message.error(
        error instanceof Error ? error.message : "Failed to link organization"
      );
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkOrganization = async () => {
    setLinking(true);
    try {
      const { error } = await supabase
        .from("company")
        .update({
          leancustoms_organization_id: null,
          leancustoms_contract_id: null,
        })
        .eq("id", company.id);

      if (error) throw error;

      message.success("Organization unlinked successfully");
      setLinkedOrg(null);

      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error("Error unlinking organization:", error);
      message.error("Failed to unlink organization");
    } finally {
      setLinking(false);
    }
  };

  // Check if organization is a suggested match
  const isSuggestedMatch = (org: LeanCustomsOrganization): boolean => {
    return suggestedOrganizations.some((suggested) => suggested.id === org.id);
  };

  // Get match quality for an organization
  const getMatchQuality = (
    org: LeanCustomsOrganization
  ): {
    score: number;
    matches: string[];
  } => {
    const matches: string[] = [];
    let score = 0;

    if (company.coc_number && org.coc_number === String(company.coc_number)) {
      matches.push("COC");
      score += 2;
    }
    if (company.eori_number && org.eori_number === company.eori_number) {
      matches.push("EORI");
      score += 2;
    }

    return { score, matches };
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name: string, record: LeanCustomsOrganization) => {
        const isSuggested = isSuggestedMatch(record);
        return (
          <Space>
            {isSuggested && (
              <Tag color="green" icon={<CheckOutlined />}>
                Suggested Match
              </Tag>
            )}
            <span style={{ fontWeight: isSuggested ? 600 : 400 }}>{name}</span>
          </Space>
        );
      },
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type: string) => <Tag>{type}</Tag>,
    },
    {
      title: "Country",
      dataIndex: "country_code",
      key: "country_code",
    },
    {
      title: "EORI",
      dataIndex: "eori_number",
      key: "eori_number",
      render: (eori: string, record: LeanCustomsOrganization) => {
        const isMatch = company.eori_number && eori === company.eori_number;
        return (
          <span
            style={{
              color: isMatch ? "#52c41a" : undefined,
              fontWeight: isMatch ? 600 : 400,
            }}
          >
            {eori || "-"}
            {isMatch && " ✓"}
          </span>
        );
      },
    },
    {
      title: "COC",
      dataIndex: "coc_number",
      key: "coc_number",
      render: (coc: string, record: LeanCustomsOrganization) => {
        const isMatch =
          company.coc_number && coc === String(company.coc_number);
        return (
          <span
            style={{
              color: isMatch ? "#52c41a" : undefined,
              fontWeight: isMatch ? 600 : 400,
            }}
          >
            {coc || "-"}
            {isMatch && " ✓"}
          </span>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_: unknown, record: LeanCustomsOrganization) => (
        <Space>
          <Button
            size="small"
            onClick={() => {
              setSelectedOrg(record);
              setViewOrgModalVisible(true);
            }}
          >
            View
          </Button>
          <Button
            type={isSuggestedMatch(record) ? "primary" : "default"}
            size="small"
            icon={<LinkOutlined />}
            onClick={() => handleLinkOrganization(record)}
            loading={linking}
          >
            Link
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card
        title="LeanCustoms Integration"
        style={{ marginBottom: 16 }}
        extra={
          readOnly ? (
            company.leancustoms_organization_id ? (
              <Button
                type="link"
                href={`${LEANCUSTOMS_ORGANIZATION_BASE}/${company.leancustoms_organization_id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                View in LeanCustoms →
              </Button>
            ) : null
          ) : (
            <Space>
              {company.leancustoms_organization_id && (
                <>
                  <Button
                    type="link"
                    href={`${LEANCUSTOMS_ORGANIZATION_BASE}/${company.leancustoms_organization_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View in LeanCustoms →
                  </Button>
                  <Button
                    danger
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={handleUnlinkOrganization}
                    loading={linking}
                  >
                    Unlink
                  </Button>
                </>
              )}
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={() => {
                  setSearchModalVisible(true);
                  setSuggestedOrganizations([]);
                  // Auto-presearch when opening modal if company has COC/EORI
                  if (company.coc_number || company.eori_number) {
                    presearchOrganizations();
                  }
                  searchOrganizations();
                }}
              >
                {company.leancustoms_organization_id
                  ? "Change Organization"
                  : "Link Organization"}
              </Button>
            </Space>
          )
        }
      >
        {loadingLinkedOrg ? (
          <Spin />
        ) : linkedOrg ? (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Name" span={2}>
              {linkedOrg.name}
            </Descriptions.Item>
            <Descriptions.Item label="Type">
              <Tag>{linkedOrg.type}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Country">
              {linkedOrg.country_code}
            </Descriptions.Item>
            <Descriptions.Item label="EORI Number">
              {linkedOrg.eori_number || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="COC Number">
              {linkedOrg.coc_number || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="VAT Number">
              {linkedOrg.vat_number || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Address" span={2}>
              {linkedOrg.street}, {linkedOrg.postcode} {linkedOrg.place}
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <Empty
            description="No organization linked"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}

        {/* Contracts Section */}
        {linkedOrg && (
          <div style={{ marginTop: 24 }}>
            <Divider orientation="left">Contracts</Divider>
            {loadingContracts ? (
              <Spin />
            ) : contracts.length > 0 ? (
              <Table
                dataSource={contracts}
                rowKey="id"
                pagination={false}
                size="small"
                expandable={{
                  expandedRowRender: (record) => {
                    const signers = contractSigners[record.id] || [];
                    const isLoadingSigners = loadingSigners[record.id] || false;
                    const supportDocuments =
                      contractSupportDocuments[record.id] || [];
                    const isLoadingDocuments =
                      loadingSupportDocuments[record.id] || false;

                    // Load signers and documents when expanding if not already loaded
                    if (!contractSigners[record.id] && !isLoadingSigners) {
                      loadContractSigners(record.id);
                    }
                    if (
                      !contractSupportDocuments[record.id] &&
                      !isLoadingDocuments
                    ) {
                      loadContractSupportDocuments(record.id);
                    }

                    return (
                      <div style={{ padding: "16px 0" }}>
                        {/* Signers Section */}
                        <div style={{ marginBottom: 24 }}>
                          <h4 style={{ marginBottom: 12 }}>Signers</h4>
                          {isLoadingSigners ? (
                            <Spin />
                          ) : signers.length > 0 ? (
                            <Table
                              dataSource={signers}
                              rowKey="id"
                              pagination={false}
                              size="small"
                              columns={[
                                {
                                  title: "Name",
                                  dataIndex: "name",
                                  key: "name",
                                  render: (
                                    name: string,
                                    record: LeanCustomsContractSigner
                                  ) => (
                                    <Button
                                      type="link"
                                      onClick={() =>
                                        handleViewSignerDetails(record)
                                      }
                                      style={{ padding: 0 }}
                                    >
                                      {name}
                                    </Button>
                                  ),
                                },
                                {
                                  title: "Position",
                                  dataIndex: "position",
                                  key: "position",
                                  render: (pos: string) => pos || "-",
                                },
                                {
                                  title: "Email",
                                  dataIndex: "email",
                                  key: "email",
                                  render: (email: string) => email || "-",
                                },
                                {
                                  title: "Status",
                                  dataIndex: "status",
                                  key: "status",
                                  render: (status: string) => (
                                    <Tag
                                      color={
                                        status === "signed"
                                          ? "green"
                                          : status === "pending"
                                          ? "orange"
                                          : "default"
                                      }
                                    >
                                      {status || "unknown"}
                                    </Tag>
                                  ),
                                },
                                {
                                  title: "Signed At",
                                  dataIndex: "signed_at",
                                  key: "signed_at",
                                  render: (date: string) =>
                                    date
                                      ? new Date(date).toLocaleDateString()
                                      : "-",
                                },
                                {
                                  title: "Verified At",
                                  dataIndex: "verified_at",
                                  key: "verified_at",
                                  render: (date: string) =>
                                    date
                                      ? new Date(date).toLocaleDateString()
                                      : "-",
                                },
                              ]}
                            />
                          ) : (
                            <Empty
                              description="No signers"
                              image={Empty.PRESENTED_IMAGE_SIMPLE}
                              style={{ padding: "20px 0" }}
                            />
                          )}
                        </div>

                        {/* Support Documents Section */}
                        <Divider />
                        <div>
                          <h4 style={{ marginBottom: 12 }}>
                            Support Documents
                          </h4>
                          {isLoadingDocuments ? (
                            <Spin />
                          ) : supportDocuments.length > 0 ? (
                            <Table
                              dataSource={supportDocuments}
                              rowKey="id"
                              pagination={false}
                              size="small"
                              columns={[
                                {
                                  title: "Filename",
                                  dataIndex: "filename",
                                  key: "filename",
                                },
                                {
                                  title: "Type",
                                  dataIndex: "content_type",
                                  key: "content_type",
                                },
                                {
                                  title: "Size",
                                  dataIndex: "byte_size",
                                  key: "byte_size",
                                  render: (size: number) => {
                                    if (size < 1024) return `${size} B`;
                                    if (size < 1024 * 1024)
                                      return `${(size / 1024).toFixed(2)} KB`;
                                    return `${(size / (1024 * 1024)).toFixed(
                                      2
                                    )} MB`;
                                  },
                                },
                                {
                                  title: "Date Uploaded",
                                  dataIndex: "created_at",
                                  key: "created_at",
                                  render: (date: string) => {
                                    if (!date) return "-";
                                    return new Date(date).toLocaleDateString();
                                  },
                                },
                                {
                                  title: "Action",
                                  key: "action",
                                  render: (
                                    _: unknown,
                                    doc: LeanCustomsContractSupportDocument
                                  ) => (
                                    <Button
                                      type="link"
                                      size="small"
                                      icon={<DownloadOutlined />}
                                      onClick={() =>
                                        handleDownloadDocument(record.id, doc)
                                      }
                                    >
                                      Download
                                    </Button>
                                  ),
                                },
                              ]}
                            />
                          ) : (
                            <Empty
                              description="No support documents"
                              image={Empty.PRESENTED_IMAGE_SIMPLE}
                              style={{ padding: "20px 0" }}
                            />
                          )}
                        </div>
                      </div>
                    );
                  },
                  rowExpandable: () => true,
                }}
                columns={[
                  {
                    title: "Contract Reference",
                    dataIndex: "contract_reference",
                    key: "contract_reference",
                    render: (ref: string) => ref || "-",
                  },
                  {
                    title: "Status",
                    dataIndex: "status",
                    key: "status",
                    render: (status: string) => (
                      <Tag
                        color={
                          status === "signed"
                            ? "green"
                            : status === "active"
                            ? "blue"
                            : status === "pending"
                            ? "orange"
                            : "default"
                        }
                      >
                        {status}
                      </Tag>
                    ),
                  },
                  {
                    title: "Agent",
                    dataIndex: "agent_name",
                    key: "agent_name",
                  },
                  {
                    title: "Requested By",
                    dataIndex: "requested_by_name",
                    key: "requested_by_name",
                    render: (name: string) => name || "-",
                  },
                  {
                    title: "Start Date",
                    dataIndex: "start_date",
                    key: "start_date",
                    render: (date: string) =>
                      date ? new Date(date).toLocaleDateString() : "-",
                  },
                  {
                    title: "End Date",
                    dataIndex: "end_date",
                    key: "end_date",
                    render: (date: string) =>
                      date ? new Date(date).toLocaleDateString() : "-",
                  },
                  {
                    title: "Signed On",
                    dataIndex: "signed_on",
                    key: "signed_on",
                    render: (date: string) =>
                      date ? new Date(date).toLocaleDateString() : "-",
                  },
                  {
                    title: "Digitally Signed",
                    dataIndex: "digitally_signed",
                    key: "digitally_signed",
                    render: (signed: boolean) =>
                      signed ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>,
                  },
                ]}
              />
            ) : (
              <Empty
                description="No contracts found"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </div>
        )}

        {/* Declarations Section */}
        {linkedOrg && (
          <div style={{ marginTop: 24 }}>
            <Divider orientation="left">
              {bookingId != null
                ? "Declaration for this booking"
                : "Declarations"}
            </Divider>
            {loadingDeclarations ? (
              <Spin />
            ) : declarations.length > 0 ? (
              bookingId != null ? (
                <Table
                  dataSource={declarations}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  onRow={(record) => ({
                    onClick: () => {
                      setSelectedDeclaration(record);
                      setDeclarationModalVisible(true);
                      setDeclarationInvoices([]);
                      if (record.id) {
                        loadDeclarationInvoices(record.id);
                      }
                    },
                    style: { cursor: "pointer" },
                  })}
                  columns={[
                    {
                      title: "Reference",
                      dataIndex: "reference",
                      key: "reference",
                      render: (ref: string) =>
                        ref ? (
                          <BookingReferenceText text={ref} />
                        ) : (
                          "-"
                        ),
                    },
                    {
                      title: "Type",
                      dataIndex: "declaration_type",
                      key: "declaration_type",
                      render: (type: string) => type || "-",
                    },
                    {
                      title: "Date",
                      dataIndex: "date",
                      key: "date",
                      render: (date: string) =>
                        date ? new Date(date).toLocaleDateString() : "-",
                    },
                    {
                      title: "Action",
                      key: "action",
                      render: (
                        _: unknown,
                        record: LeanCustomsDeclaration
                      ) => (
                        <Button
                          type="link"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDeclaration(record);
                            setDeclarationModalVisible(true);
                            setDeclarationInvoices([]);
                            if (record.id) {
                              loadDeclarationInvoices(record.id);
                            }
                          }}
                        >
                          View Details
                        </Button>
                      ),
                    },
                  ]}
                />
              ) : (
                (() => {
                  // Group declarations by status (company edit: all declarations)
                  const groupedByStatus = declarations.reduce(
                    (acc, declaration) => {
                      const status = declaration.dms_status || "unknown";
                      if (!acc[status]) {
                        acc[status] = [];
                      }
                      acc[status].push(declaration);
                      return acc;
                    },
                    {} as Record<string, LeanCustomsDeclaration[]>
                  );

                  const sortedStatuses = Object.keys(groupedByStatus).sort(
                    (a, b) => {
                      const aIndex = DECLARATION_STATUS_ORDER.indexOf(a as any);
                      const bIndex = DECLARATION_STATUS_ORDER.indexOf(b as any);
                      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
                      if (aIndex !== -1) return -1;
                      if (bIndex !== -1) return 1;
                      return a.localeCompare(b);
                    }
                  );

                  const collapseItems = sortedStatuses.map((status) => {
                    const statusDeclarations = groupedByStatus[status];
                    const statusColor =
                      DECLARATION_STATUS_COLORS[status] || "default";
                    const statusLabel =
                      DECLARATION_STATUS_LABELS[status] || status;

                    return {
                      key: status,
                      label: (
                        <Space>
                          <Tag color={statusColor}>{statusLabel}</Tag>
                          <span>({statusDeclarations.length})</span>
                        </Space>
                      ),
                      children: (
                        <Table
                          dataSource={statusDeclarations}
                          rowKey="id"
                          pagination={false}
                          size="small"
                          onRow={(record) => ({
                            onClick: () => {
                              setSelectedDeclaration(record);
                              setDeclarationModalVisible(true);
                              setDeclarationInvoices([]);
                              if (record.id) {
                                loadDeclarationInvoices(record.id);
                              }
                            },
                            style: { cursor: "pointer" },
                          })}
                          columns={[
                            {
                              title: "Reference",
                              dataIndex: "reference",
                              key: "reference",
                              render: (ref: string) =>
                                ref ? (
                                  <BookingReferenceText text={ref} />
                                ) : (
                                  "-"
                                ),
                            },
                            {
                              title: "Type",
                              dataIndex: "declaration_type",
                              key: "declaration_type",
                              render: (type: string) => type || "-",
                            },
                            {
                              title: "Date",
                              dataIndex: "date",
                              key: "date",
                              render: (date: string) =>
                                date
                                  ? new Date(date).toLocaleDateString()
                                  : "-",
                            },
                            {
                              title: "Action",
                              key: "action",
                              render: (
                                _: unknown,
                                record: LeanCustomsDeclaration
                              ) => (
                                <Button
                                  type="link"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedDeclaration(record);
                                    setDeclarationModalVisible(true);
                                    setDeclarationInvoices([]);
                                    if (record.id) {
                                      loadDeclarationInvoices(record.id);
                                    }
                                  }}
                                >
                                  View Details
                                </Button>
                              ),
                            },
                          ]}
                        />
                      ),
                    };
                  });

                  return (
                    <Collapse
                      items={collapseItems}
                      defaultActiveKey={["accepted", "pending"]}
                    />
                  );
                })()
              )
            ) : (
              <Empty
                description={
                  bookingId != null ? (
                    <span>
                      No declaration found for this booking.
                      {CUSTOMS_PAGE_ENABLED ? (
                        <>
                          {" "}
                          <a href="/customs">View all in Customs</a>
                        </>
                      ) : null}
                    </span>
                  ) : (
                    "No declarations found"
                  )
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </div>
        )}
      </Card>

      {/* Signer Details Modal */}
      <Modal
        title="Signer Details"
        open={signerModalVisible}
        onCancel={() => {
          setSignerModalVisible(false);
          setSelectedSigner(null);
        }}
        footer={null}
        width={700}
      >
        {loadingSignerDetails ? (
          <Spin />
        ) : selectedSigner ? (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Name" span={2}>
              {selectedSigner.name}
            </Descriptions.Item>
            <Descriptions.Item label="Position">
              {selectedSigner.position || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {selectedSigner.email || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag
                color={
                  selectedSigner.status === "signed"
                    ? "green"
                    : selectedSigner.status === "pending"
                    ? "orange"
                    : "default"
                }
              >
                {selectedSigner.status || "unknown"}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Status Reason">
              {selectedSigner.status_reason || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Identification Method">
              {selectedSigner.identification_method || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Document Type">
              {selectedSigner.document_type || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Document Number">
              {selectedSigner.document_number || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Signed At">
              {selectedSigner.signed_at
                ? new Date(selectedSigner.signed_at).toLocaleString()
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Verified At">
              {selectedSigner.verified_at
                ? new Date(selectedSigner.verified_at).toLocaleString()
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Status Changed At">
              {selectedSigner.status_changed_at
                ? new Date(selectedSigner.status_changed_at).toLocaleString()
                : "-"}
            </Descriptions.Item>
            {selectedSigner.document && (
              <Descriptions.Item label="Document" span={2}>
                <Tag>
                  Document ID:{" "}
                  {(selectedSigner.document as { id?: number })?.id || "N/A"}
                </Tag>
              </Descriptions.Item>
            )}
          </Descriptions>
        ) : (
          <Empty description="No signer details available" />
        )}
      </Modal>

      {/* Search Organizations Modal */}
      <Modal
        title="Search LeanCustoms Organizations"
        open={searchModalVisible}
        onCancel={() => {
          setSearchModalVisible(false);
          setSearchQuery("");
          setOrganizations([]);
          setSuggestedOrganizations([]);
        }}
        footer={null}
        width={900}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          <Space direction="vertical" style={{ width: "100%" }} size="small">
            <Space.Compact style={{ width: "100%" }}>
              <Input
                placeholder="Search by name, EORI, COC number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onPressEnter={handleSearch}
                prefix={<SearchOutlined />}
              />
              <Button type="primary" onClick={handleSearch} loading={loading}>
                Search
              </Button>
            </Space.Compact>
            <Button
              type="default"
              icon={<CheckOutlined />}
              onClick={presearchOrganizations}
              loading={loadingSuggestions}
              disabled={!company.coc_number && !company.eori_number}
              style={{ width: "100%" }}
            >
              Find Matching Organizations (by COC/EORI)
            </Button>
            {suggestedOrganizations.length > 0 && (
              <div
                style={{
                  marginTop: 8,
                  padding: 8,
                  background: "#f0f9ff",
                  borderRadius: 4,
                }}
              >
                <strong>Suggested Matches:</strong> Found{" "}
                {suggestedOrganizations.length} organization(s) matching your
                company's COC/EORI numbers
              </div>
            )}
          </Space>

          {/* Show suggested organizations first if available */}
          {suggestedOrganizations.length > 0 && (
            <div>
              <h4 style={{ marginBottom: 8 }}>
                Suggested Matches ({suggestedOrganizations.length})
              </h4>
              <Table
                columns={columns}
                dataSource={suggestedOrganizations}
                rowKey="id"
                pagination={false}
                size="small"
                style={{ marginBottom: 16 }}
                rowClassName={(record) =>
                  isSuggestedMatch(record) ? "suggested-row" : ""
                }
              />
              <Divider>All Organizations</Divider>
            </div>
          )}

          <Table
            columns={columns}
            dataSource={organizations}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: false,
              onChange: (page) => searchOrganizations(page),
            }}
            rowClassName={(record) =>
              isSuggestedMatch(record) ? "suggested-row" : ""
            }
            locale={{
              emptyText: (
                <Empty
                  description="No organizations found"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ),
            }}
          />
        </Space>
      </Modal>

      {/* View Organization Details Modal */}
      <Modal
        title="Organization Details"
        open={viewOrgModalVisible}
        onCancel={() => {
          setViewOrgModalVisible(false);
          setSelectedOrg(null);
        }}
        footer={[
          <Button key="cancel" onClick={() => setViewOrgModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="link"
            type="primary"
            icon={<LinkOutlined />}
            onClick={() => {
              if (selectedOrg) {
                handleLinkOrganization(selectedOrg);
                setViewOrgModalVisible(false);
              }
            }}
            loading={linking}
          >
            Link This Organization
          </Button>,
        ]}
        width={600}
      >
        {selectedOrg && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="ID">
              <code>{selectedOrg.id}</code>
            </Descriptions.Item>
            <Descriptions.Item label="Name">
              {selectedOrg.name}
            </Descriptions.Item>
            <Descriptions.Item label="Type">
              <Tag>{selectedOrg.type}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Country">
              {selectedOrg.country_code}
            </Descriptions.Item>
            <Descriptions.Item label="EORI Number">
              {selectedOrg.eori_number || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="COC Number">
              {selectedOrg.coc_number || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="VAT Number">
              {selectedOrg.vat_number || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Address">
              {selectedOrg.street}, {selectedOrg.postcode} {selectedOrg.place}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* Declaration Details Modal */}
      <Modal
        title="Declaration Details"
        open={declarationModalVisible}
        onCancel={() => {
          setDeclarationModalVisible(false);
          setSelectedDeclaration(null);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setDeclarationModalVisible(false);
              setSelectedDeclaration(null);
            }}
          >
            Close
          </Button>,
        ]}
        width={800}
      >
        {selectedDeclaration && (
          <Tabs
            onChange={(key) => {
              // Load invoices when the invoices tab is selected
              if (key === "invoices" && selectedDeclaration.id) {
                if (declarationInvoices.length === 0 && !loadingInvoices) {
                  loadDeclarationInvoices(selectedDeclaration.id);
                }
              }
            }}
            items={[
              {
                key: "details",
                label: "Details",
                children: (
                  <Descriptions bordered column={2} size="small">
                    <Descriptions.Item label="Reference" span={2}>
                      {selectedDeclaration.reference ? (
                        <BookingReferenceText text={selectedDeclaration.reference} />
                      ) : (
                        "-"
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Booking number" span={2}>
                      {(selectedDeclaration as { booking_number?: string })
                        .booking_number ? (
                        <BookingReferenceText
                          text={
                            (selectedDeclaration as { booking_number?: string })
                              .booking_number
                          }
                        />
                      ) : (
                        "-"
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="MRN">
                      {selectedDeclaration.mrn || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="LRN">
                      {selectedDeclaration.lrn || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Type">
                      {selectedDeclaration.declaration_type || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Status">
                      <Tag
                        color={
                          DECLARATION_STATUS_COLORS[
                            selectedDeclaration.dms_status || ""
                          ] || "default"
                        }
                      >
                        {DECLARATION_STATUS_LABELS[
                          selectedDeclaration.dms_status || ""
                        ] ||
                          selectedDeclaration.dms_status ||
                          "unknown"}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Date">
                      {selectedDeclaration.date
                        ? new Date(selectedDeclaration.date).toLocaleDateString()
                        : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Acceptance Date">
                      {selectedDeclaration.acceptance_date
                        ? new Date(
                            selectedDeclaration.acceptance_date
                          ).toLocaleDateString()
                        : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Clearance Date">
                      {selectedDeclaration.clearance_date
                        ? new Date(
                            selectedDeclaration.clearance_date
                          ).toLocaleDateString()
                        : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Release Date">
                      {selectedDeclaration.release_date
                        ? new Date(
                            selectedDeclaration.release_date
                          ).toLocaleDateString()
                        : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Importer" span={2}>
                      {selectedDeclaration.importer_name || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Exporter" span={2}>
                      {selectedDeclaration.exporter_name || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Agent" span={2}>
                      {selectedDeclaration.agent_name || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Buyer" span={2}>
                      {selectedDeclaration.buyer_name || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Seller" span={2}>
                      {selectedDeclaration.seller_name || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Ticket Number">
                      {selectedDeclaration.ticket_number || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Ticket Sequence">
                      {selectedDeclaration.ticket_sequence_number || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="UID" span={2}>
                      <code>{selectedDeclaration.uid || "-"}</code>
                    </Descriptions.Item>
                    <Descriptions.Item label="ID" span={2}>
                      <code>{selectedDeclaration.id}</code>
                    </Descriptions.Item>
                  </Descriptions>
                ),
              },
              {
                key: "invoices",
                label: "Invoices",
                children: (
                  <div>
                    {loadingInvoices ? (
                      <Spin />
                    ) : declarationInvoices.length > 0 ? (
                      <Table
                        dataSource={declarationInvoices}
                        rowKey="id"
                        pagination={false}
                        size="small"
                        expandable={{
                          expandedRowRender: (record: LeanCustomsInvoice) => {
                            if (!record.invoice_lines || record.invoice_lines.length === 0) {
                              return <div style={{ padding: '8px 0' }}>No invoice lines</div>;
                            }
                            return (
                              <Table
                                dataSource={record.invoice_lines}
                                rowKey="id"
                                pagination={false}
                                size="small"
                                columns={[
                                  {
                                    title: "Description",
                                    dataIndex: "description",
                                    key: "description",
                                    render: (desc: string) => desc || "-",
                                  },
                                  {
                                    title: "Quantity",
                                    dataIndex: "quantity",
                                    key: "quantity",
                                    render: (qty: number) => qty || "-",
                                    align: "right" as const,
                                  },
                                  {
                                    title: "Unit Price",
                                    dataIndex: "unit_price",
                                    key: "unit_price",
                                    render: (price: string) => price || "-",
                                    align: "right" as const,
                                  },
                                  {
                                    title: "Total Excl. Tax",
                                    dataIndex: "total_price_excl_tax",
                                    key: "total_price_excl_tax",
                                    render: (price: string) => price || "-",
                                    align: "right" as const,
                                  },
                                  {
                                    title: "Total Incl. Tax",
                                    dataIndex: "total_price_incl_tax",
                                    key: "total_price_incl_tax",
                                    render: (price: string) => price || "-",
                                    align: "right" as const,
                                  },
                                ]}
                              />
                            );
                          },
                          rowExpandable: (record: LeanCustomsInvoice) => 
                            record.invoice_lines && record.invoice_lines.length > 0,
                        }}
                        columns={[
                          {
                            title: "Invoice Number",
                            dataIndex: "invoice_number",
                            key: "invoice_number",
                            render: (num: string) => num || "-",
                          },
                          {
                            title: "Invoice Date",
                            dataIndex: "invoice_date",
                            key: "invoice_date",
                            render: (date: string) =>
                              date ? new Date(date).toLocaleDateString() : "-",
                          },
                          {
                            title: "Supplier Reference",
                            dataIndex: "supplier_reference",
                            key: "supplier_reference",
                            render: (ref: string) => ref || "-",
                          },
                          {
                            title: "Customer Reference",
                            dataIndex: "customer_reference",
                            key: "customer_reference",
                            render: (ref: string) => ref || "-",
                          },
                          {
                            title: "Invoice Type",
                            dataIndex: "invoice_type",
                            key: "invoice_type",
                            render: (type: string) => type || "-",
                          },
                          {
                            title: "Billing Type",
                            dataIndex: "billing_type",
                            key: "billing_type",
                            render: (type: string) => type || "-",
                          },
                          {
                            title: "Status",
                            dataIndex: "status",
                            key: "status",
                            render: (status: string) => (
                              <Tag
                                color={status === "paid" ? "green" : "default"}
                              >
                                {status || "-"}
                              </Tag>
                            ),
                          },
                          {
                            title: "Supplier",
                            dataIndex: ["supplier", "name"],
                            key: "supplier",
                            render: (name: string) => name || "-",
                          },
                          {
                            title: "Customer",
                            dataIndex: ["customer", "name"],
                            key: "customer",
                            render: (name: string) => name || "-",
                          },
                          {
                            title: "Invoice Lines",
                            key: "invoice_lines",
                            render: (_: unknown, invoice: LeanCustomsInvoice) =>
                              invoice.invoice_lines?.length || 0,
                          },
                          {
                            title: "Documents",
                            key: "documents",
                            render: (_: unknown, invoice: LeanCustomsInvoice) =>
                              invoice.documents?.length || 0,
                          },
                          {
                            title: "Actions",
                            key: "actions",
                            width: 100,
                            render: (_: unknown, invoice: LeanCustomsInvoice) => (
                              <Button
                                type="link"
                                size="small"
                                icon={<DownloadOutlined />}
                                onClick={() => handleDownloadInvoice(invoice)}
                                disabled={!invoice.documents || invoice.documents.length === 0}
                              >
                                Download
                              </Button>
                            ),
                          },
                        ]}
                      />
                    ) : (
                      <Empty
                        description="No invoices found"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    )}
                  </div>
                ),
              },
            ]}
          />
        )}
      </Modal>
    </>
  );
};

export default AdminLeanCustomsIntegrationSection;
